import React, { useEffect, useState } from 'react';
import { Button, InputAdornment, TextField, Tooltip } from '@material-ui/core';
import { useNumberFilterStyles } from './styles';
import {
  NumberComparisonType,
  NumberFilterInputAdornmentProps,
  NumberFilterProps,
} from './types';

function NumberFilterInputAdornment({
  comparisonType,
  onChange,
  disabled,
}: NumberFilterInputAdornmentProps) {
  const classes = useNumberFilterStyles();

  return (
    <div className={classes.numberFilterAdornment}>
      <Tooltip title="Less than">
        <span>
          <Button
            disabled={disabled}
            size="small"
            onClick={() => onChange(NumberComparisonType.LESS_THAN)}
            onTouchStart={() => onChange(NumberComparisonType.LESS_THAN)}
            className={
              comparisonType === NumberComparisonType.LESS_THAN ? 'active' : ''
            }
          >
            &#60;
          </Button>
        </span>
      </Tooltip>

      <Tooltip title="Equals">
        <span>
          <Button
            disabled={disabled}
            size="small"
            onClick={() => onChange(NumberComparisonType.EQUAL)}
            onTouchStart={() => onChange(NumberComparisonType.EQUAL)}
            className={
              comparisonType === NumberComparisonType.EQUAL ? 'active' : ''
            }
          >
            =
          </Button>
        </span>
      </Tooltip>
      <Tooltip title="Greater than">
        <span>
          <Button
            disabled={disabled}
            size="small"
            onClick={() => onChange(NumberComparisonType.GREATER_THAN)}
            onTouchStart={() => onChange(NumberComparisonType.GREATER_THAN)}
            className={
              comparisonType === NumberComparisonType.GREATER_THAN
                ? 'active'
                : ''
            }
          >
            &#62;
          </Button>
        </span>
      </Tooltip>
    </div>
  );
}

function NumberFilter(props: NumberFilterProps) {
  const { addFilter, onFocus, onBlur, disabled, ...restProps } = props;
  const [comparisonType, setComparisonType] =
    useState<NumberComparisonType | null>(null);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [value, setValue] = useState<string>('');
  const [helperText, setHelperText] = useState<string | null>(null);

  useEffect(() => {
    setHelperText(null);
  }, [comparisonType, value]);

  const handleTextFieldKeyPress = (e: any) => {
    if (e.key === 'Enter' && value && comparisonType) {
      setValue('');

      if (comparisonType === NumberComparisonType.GREATER_THAN) {
        addFilter([
          {
            category: 'measurementGreaterThan',
            value,
          },
        ]);
      } else if (comparisonType === NumberComparisonType.LESS_THAN) {
        addFilter([{ category: 'measurementLessThan', value }]);
      } else if (comparisonType === NumberComparisonType.EQUAL) {
        addFilter([
          {
            category: 'measurementEquals',
            value: value,
          },
        ]);
      }

      setValue('');
      setComparisonType(null);
    } else {
      let helperText = '';

      if (!comparisonType) {
        helperText += 'Comparison type should be selected.';
      }

      if (!value) {
        helperText += 'Value should exist.';
      }

      setHelperText(helperText);
    }
  };

  const handleFocus = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    onFocus && onFocus(event);
    setIsFocused(true);
  };

  const handleBlur = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    onBlur && onBlur(event);
    setIsFocused(false);
  };

  return (
    <TextField
      {...restProps}
      disabled={disabled}
      variant="outlined"
      margin="dense"
      type="number"
      value={value}
      helperText={helperText}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={(e) => setValue(e.target.value)}
      onKeyPress={(e) => handleTextFieldKeyPress(e)}
      InputLabelProps={{
        shrink: isFocused || !!value,
        style: {
          marginLeft: isFocused || !!value ? '0px' : '75px',
        },
      }}
      InputProps={{
        style: { paddingLeft: '0px' },
        startAdornment: (
          <InputAdornment position="start">
            <NumberFilterInputAdornment
              disabled={disabled}
              comparisonType={comparisonType}
              onChange={setComparisonType}
            />
          </InputAdornment>
        ),
      }}
    />
  );
}

export default NumberFilter;
